import { useEffect, useState } from 'react';
import { FaCalendar, FaEnvelope, FaPhone, FaUser, FaUsers } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { bookingsAPI, toursAPI } from '../services/api';
import './Booking.css';

const Booking = () => {
  const { tourId } = useParams();
  const navigate = useNavigate();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    numberOfPeople: 1,
    travelDate: '',
    specialRequests: ''
  });

  useEffect(() => {
    fetchTour();
  }, [tourId]);

  const fetchTour = async () => {
    try {
      const response = await toursAPI.getById(tourId);
      setTour(response.data.data);
    } catch (error) {
      console.error('Error fetching tour:', error);
      setMessage({ type: 'error', text: 'Tour not found' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      await bookingsAPI.create({
        tourId: tour._id,
        customer: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone
        },
        numberOfPeople: parseInt(formData.numberOfPeople),
        travelDate: formData.travelDate,
        specialRequests: formData.specialRequests
      });

      setMessage({
        type: 'success',
        text: 'Booking submitted successfully! We will contact you shortly.'
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        numberOfPeople: 1,
        travelDate: '',
        specialRequests: ''
      });

      // Redirect after 3 seconds
      setTimeout(() => {
        navigate('/tours');
      }, 3000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Booking failed. Please try again.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="spinner" style={{ margin: '100px auto' }}></div>;
  }

  if (!tour) {
    return (
      <div className="container section text-center">
        <h2>Tour not found</h2>
        <button onClick={() => navigate('/tours')} className="btn btn-primary mt-2">
          Back to Tours
        </button>
      </div>
    );
  }

  const totalPrice = tour.price * formData.numberOfPeople;

  return (
    <div className="booking-page">
      <div className="page-header">
        <div className="container">
          <h1 className="page-title">Book Your Tour</h1>
          <p className="page-subtitle">Complete the form below to reserve your spot</p>
        </div>
      </div>

      <div className="container section">
        <div className="booking-grid">
          {/* Booking Form */}
          <div className="booking-form-container">
            <h2>Booking Information</h2>

            {message.text && (
              <div className={`alert alert-${message.type}`}>
                {message.text}
              </div>
            )}

            <form onSubmit={handleSubmit} className="booking-form">
              <div className="form-group">
                <label className="form-label">
                  <FaUser /> Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form-control"
                  placeholder="John Doe"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <FaEnvelope /> Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-control"
                  placeholder="john@example.com"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <FaPhone /> Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="form-control"
                  placeholder="+1 234 567 890"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    <FaUsers /> Number of People *
                  </label>
                  <input
                    type="number"
                    name="numberOfPeople"
                    value={formData.numberOfPeople}
                    onChange={handleChange}
                    required
                    min="1"
                    max={tour.maxGroupSize}
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <FaCalendar /> Travel Date *
                  </label>
                  <input
                    type="date"
                    name="travelDate"
                    value={formData.travelDate}
                    onChange={handleChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Special Requests (Optional)
                </label>
                <textarea
                  name="specialRequests"
                  value={formData.specialRequests}
                  onChange={handleChange}
                  className="form-control"
                  rows="4"
                  placeholder="Any special requirements or requests..."
                ></textarea>
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: '100%', padding: '15px', fontSize: '1.1rem' }}
                disabled={submitting}
              >
                {submitting ? 'Processing...' : `Book Now - ₹${totalPrice}`}
              </button>

              <p className="form-note">
                * By clicking "Book Now", you agree to our terms and conditions
              </p>
            </form>
          </div>

          {/* Tour Summary */}
          <div className="tour-summary-container">
            <div className="tour-summary card">
              <h3>Booking Summary</h3>
              <img
                src={tour.images?.[0] || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=500'}
                alt={tour.title}
                className="summary-image"
              />
              <h4>{tour.title}</h4>
              <p className="summary-destination">{tour.destination}</p>

              <div className="summary-details">
                <div className="summary-row">
                  <span>Duration:</span>
                  <strong>{tour.duration}</strong>
                </div>
                <div className="summary-row">
                  <span>Price per person:</span>
                  <strong>₹{tour.price}</strong>
                </div>
                <div className="summary-row">
                  <span>Number of people:</span>
                  <strong>{formData.numberOfPeople}</strong>
                </div>
                <div className="summary-row">
                  <span>Travel date:</span>
                  <strong>{formData.travelDate || 'Not selected'}</strong>
                </div>
              </div>

              <div className="summary-total">
                <span>Total Price:</span>
                <strong>₹{totalPrice}</strong>
              </div>

              <div className="summary-note">
                <p>✓ Free cancellation up to 24 hours</p>
                <p>✓ Instant confirmation</p>
                <p>✓ Professional guides</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Booking;
